// src/features/jurisdictions/jurisdictionService.ts
// Phase 1.1: Firestore service layer for Jurisdiction management

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  QueryConstraint,
} from "firebase/firestore";
import { db } from "@/firebase";
import type {
  Jurisdiction,
  JurisdictionRuleGuide,
  JurisdictionFilter,
  JurisdictionSearchResult,
} from "@/types/jurisdiction";

const COLLECTION_NAME = "jurisdictions";
const GUIDES_COLLECTION_NAME = "jurisdictionRuleGuides";

/**
 * Get all jurisdictions with optional filtering
 */
export async function getJurisdictions(
  filter?: JurisdictionFilter
): Promise<Jurisdiction[]> {
  const constraints: QueryConstraint[] = [];

  if (filter?.country) {
    constraints.push(where("country", "==", filter.country));
  }
  if (filter?.state) {
    constraints.push(where("state", "==", filter.state));
  }
  if (filter?.county) {
    constraints.push(where("county", "==", filter.county));
  }
  if (filter?.courtType) {
    constraints.push(where("courtType", "==", filter.courtType));
  }

  // Only add ordering if we have filters (to avoid index requirements)
  // When filtering, order by hierarchy for consistent results
  if (constraints.length > 0) {
    constraints.push(orderBy("state", "asc"));
    constraints.push(orderBy("county", "asc"));
  }

  const q = query(collection(db, COLLECTION_NAME), ...constraints);
  const snapshot = await getDocs(q);

  const results = snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Jurisdiction[];

  // If no filters applied, sort in memory
  if (constraints.length === 0) {
    return results.sort((a, b) => {
      if (a.state !== b.state) {
        return (a.state || '').localeCompare(b.state || '');
      }
      return (a.county || '').localeCompare(b.county || '');
    });
  }

  return results;
}

/**
 * Get a single jurisdiction by ID
 */
export async function getJurisdiction(
  jurisdictionId: string
): Promise<Jurisdiction | null> {
  const docRef = doc(db, COLLECTION_NAME, jurisdictionId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return null;
  }

  return {
    id: docSnap.id,
    ...docSnap.data(),
  } as Jurisdiction;
}

/**
 * Get jurisdictions organized by hierarchy
 * Returns: State -> County -> Division structure
 */
export async function getJurisdictionsHierarchy(): Promise<
  Map<string, Map<string, Jurisdiction[]>>
> {
  const jurisdictions = await getJurisdictions();
  const hierarchy = new Map<string, Map<string, Jurisdiction[]>>();

  for (const jurisdiction of jurisdictions) {
    if (!hierarchy.has(jurisdiction.state)) {
      hierarchy.set(jurisdiction.state, new Map());
    }

    const stateMap = hierarchy.get(jurisdiction.state)!;
    const countyKey = jurisdiction.county || "__statewide__";

    if (!stateMap.has(countyKey)) {
      stateMap.set(countyKey, []);
    }

    stateMap.get(countyKey)!.push(jurisdiction);
  }

  return hierarchy;
}

/**
 * Search jurisdictions by name (for autocomplete)
 */
export async function searchJurisdictions(
  searchTerm: string
): Promise<JurisdictionSearchResult[]> {
  // Note: Firestore doesn't support full-text search natively
  // For production, consider Algolia integration
  // For MVP, we'll fetch all and filter client-side

  const jurisdictions = await getJurisdictions();
  const lowerSearch = searchTerm.toLowerCase();

  return jurisdictions
    .filter(
      (j) =>
        j.displayName.toLowerCase().includes(lowerSearch) ||
        j.shortName.toLowerCase().includes(lowerSearch) ||
        j.county?.toLowerCase().includes(lowerSearch) ||
        j.state.toLowerCase().includes(lowerSearch)
    )
    .map((j) => ({
      id: j.id,
      displayName: j.displayName,
      shortName: j.shortName,
      state: j.state,
      county: j.county,
      courtType: j.courtType,
    }))
    .slice(0, 10); // Limit results
}

/**
 * Create a new jurisdiction (Admin only)
 */
export async function createJurisdiction(
  data: Omit<Jurisdiction, "id" | "createdAt" | "updatedAt">,
  createdBy: string
): Promise<string> {
  const now = Timestamp.now();

  const jurisdictionData = {
    ...data,
    createdAt: now,
    updatedAt: now,
    createdBy,
    isActive: data.isActive ?? true,
  };

  const docRef = await addDoc(
    collection(db, COLLECTION_NAME),
    jurisdictionData
  );
  return docRef.id;
}

/**
 * Update an existing jurisdiction (Admin only)
 */
export async function updateJurisdiction(
  jurisdictionId: string,
  data: Partial<Omit<Jurisdiction, "id" | "createdAt" | "createdBy">>
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, jurisdictionId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Delete a jurisdiction (Admin only - use with caution)
 */
export async function deleteJurisdiction(
  jurisdictionId: string
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, jurisdictionId);
  await deleteDoc(docRef);
}

// ============================================
// JURISDICTION RULE GUIDES
// ============================================

/**
 * Get rule guides for a specific jurisdiction
 */
export async function getRuleGuides(
  jurisdictionId: string,
  activeOnly: boolean = true
): Promise<JurisdictionRuleGuide[]> {
  const constraints: QueryConstraint[] = [
    where("jurisdictionId", "==", jurisdictionId),
  ];

  if (activeOnly) {
    constraints.push(where("isActive", "==", true));
  }

  constraints.push(orderBy("priority", "desc"));

  const q = query(collection(db, GUIDES_COLLECTION_NAME), ...constraints);
  const snapshot = await getDocs(q);

  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as JurisdictionRuleGuide[];
}

/**
 * Get rule guides for a specific form in a jurisdiction
 */
export async function getRuleGuidesForForm(
  jurisdictionId: string,
  formId: string
): Promise<JurisdictionRuleGuide[]> {
  const allGuides = await getRuleGuides(jurisdictionId, true);

  return allGuides.filter(
    (guide) =>
      !guide.appliesTo || // If appliesTo is empty, applies to all
      guide.appliesTo.includes(formId)
  );
}

/**
 * Create a new rule guide (Admin only)
 */
export async function createRuleGuide(
  data: Omit<JurisdictionRuleGuide, "id" | "createdAt" | "updatedAt">,
  createdBy: string
): Promise<string> {
  const now = Timestamp.now();

  const guideData = {
    ...data,
    createdAt: now,
    updatedAt: now,
    createdBy,
    isActive: data.isActive ?? true,
  };

  const docRef = await addDoc(collection(db, GUIDES_COLLECTION_NAME), guideData);
  return docRef.id;
}

/**
 * Update an existing rule guide (Admin only)
 */
export async function updateRuleGuide(
  guideId: string,
  data: Partial<
    Omit<JurisdictionRuleGuide, "id" | "createdAt" | "createdBy">
  >
): Promise<void> {
  const docRef = doc(db, GUIDES_COLLECTION_NAME, guideId);
  await updateDoc(docRef, {
    ...data,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Delete a rule guide (Admin only)
 */
export async function deleteRuleGuide(guideId: string): Promise<void> {
  const docRef = doc(db, GUIDES_COLLECTION_NAME, guideId);
  await deleteDoc(docRef);
}

// ============================================
// THREE-TIER JURISDICTION SCHEMA (V2)
// ============================================
// New collections: jurisdictionStates, jurisdictionUnits, courts
// Replaces legacy "jurisdictions" collection

// ISO 3166-1 country name lookup for dynamically discovered country codes
export const COUNTRY_NAMES: Record<string, string> = {
  AD: 'Andorra', AE: 'United Arab Emirates', AF: 'Afghanistan', AG: 'Antigua and Barbuda',
  AL: 'Albania', AM: 'Armenia', AO: 'Angola', AR: 'Argentina', AT: 'Austria',
  AU: 'Australia', AZ: 'Azerbaijan', BA: 'Bosnia and Herzegovina', BB: 'Barbados',
  BD: 'Bangladesh', BE: 'Belgium', BF: 'Burkina Faso', BG: 'Bulgaria', BH: 'Bahrain',
  BI: 'Burundi', BJ: 'Benin', BN: 'Brunei', BO: 'Bolivia', BR: 'Brazil',
  BS: 'Bahamas', BT: 'Bhutan', BW: 'Botswana', BY: 'Belarus', BZ: 'Belize',
  CA: 'Canada', CD: 'Congo (DRC)', CF: 'Central African Republic', CG: 'Congo',
  CH: 'Switzerland', CI: "Côte d'Ivoire", CL: 'Chile', CM: 'Cameroon', CN: 'China',
  CO: 'Colombia', CR: 'Costa Rica', CU: 'Cuba', CV: 'Cape Verde', CY: 'Cyprus',
  CZ: 'Czech Republic', DE: 'Germany', DJ: 'Djibouti', DK: 'Denmark', DM: 'Dominica',
  DO: 'Dominican Republic', DZ: 'Algeria', EC: 'Ecuador', EE: 'Estonia', EG: 'Egypt',
  ER: 'Eritrea', ES: 'Spain', ET: 'Ethiopia', FI: 'Finland', FJ: 'Fiji',
  FM: 'Micronesia', FR: 'France', GA: 'Gabon', GB: 'United Kingdom', GD: 'Grenada',
  GE: 'Georgia', GH: 'Ghana', GM: 'Gambia', GN: 'Guinea', GQ: 'Equatorial Guinea',
  GR: 'Greece', GT: 'Guatemala', GW: 'Guinea-Bissau', GY: 'Guyana', HN: 'Honduras',
  HR: 'Croatia', HT: 'Haiti', HU: 'Hungary', ID: 'Indonesia', IE: 'Ireland',
  IL: 'Israel', IN: 'India', IQ: 'Iraq', IR: 'Iran', IS: 'Iceland',
  IT: 'Italy', JM: 'Jamaica', JO: 'Jordan', JP: 'Japan', KE: 'Kenya',
  KG: 'Kyrgyzstan', KH: 'Cambodia', KI: 'Kiribati', KM: 'Comoros', KN: 'Saint Kitts and Nevis',
  KP: 'North Korea', KR: 'South Korea', KW: 'Kuwait', KZ: 'Kazakhstan', LA: 'Laos',
  LB: 'Lebanon', LC: 'Saint Lucia', LI: 'Liechtenstein', LK: 'Sri Lanka', LR: 'Liberia',
  LS: 'Lesotho', LT: 'Lithuania', LU: 'Luxembourg', LV: 'Latvia', LY: 'Libya',
  MA: 'Morocco', MC: 'Monaco', MD: 'Moldova', ME: 'Montenegro', MG: 'Madagascar',
  MH: 'Marshall Islands', MK: 'North Macedonia', ML: 'Mali', MM: 'Myanmar', MN: 'Mongolia',
  MR: 'Mauritania', MT: 'Malta', MU: 'Mauritius', MV: 'Maldives', MW: 'Malawi',
  MX: 'Mexico', MY: 'Malaysia', MZ: 'Mozambique', NA: 'Namibia', NE: 'Niger',
  NG: 'Nigeria', NI: 'Nicaragua', NL: 'Netherlands', NO: 'Norway', NP: 'Nepal',
  NR: 'Nauru', NZ: 'New Zealand', OM: 'Oman', PA: 'Panama', PE: 'Peru',
  PG: 'Papua New Guinea', PH: 'Philippines', PK: 'Pakistan', PL: 'Poland',
  PT: 'Portugal', PW: 'Palau', PY: 'Paraguay', QA: 'Qatar', RO: 'Romania',
  RS: 'Serbia', RU: 'Russia', RW: 'Rwanda', SA: 'Saudi Arabia', SB: 'Solomon Islands',
  SC: 'Seychelles', SD: 'Sudan', SE: 'Sweden', SG: 'Singapore', SI: 'Slovenia',
  SK: 'Slovakia', SL: 'Sierra Leone', SM: 'San Marino', SN: 'Senegal', SO: 'Somalia',
  SR: 'Suriname', SS: 'South Sudan', ST: 'São Tomé and Príncipe', SV: 'El Salvador',
  SY: 'Syria', SZ: 'Eswatini', TD: 'Chad', TG: 'Togo', TH: 'Thailand',
  TJ: 'Tajikistan', TL: 'Timor-Leste', TM: 'Turkmenistan', TN: 'Tunisia', TO: 'Tonga',
  TR: 'Turkey', TT: 'Trinidad and Tobago', TV: 'Tuvalu', TZ: 'Tanzania', UA: 'Ukraine',
  UG: 'Uganda', US: 'United States', UY: 'Uruguay', UZ: 'Uzbekistan', VA: 'Vatican City',
  VC: 'Saint Vincent and the Grenadines', VE: 'Venezuela', VN: 'Vietnam', VU: 'Vanuatu',
  WS: 'Samoa', YE: 'Yemen', ZA: 'South Africa', ZM: 'Zambia', ZW: 'Zimbabwe',
};

// Canonical countries supported by the jurisdiction engine
export const SUPPORTED_COUNTRIES = [
  { code: 'US', label: 'United States' },
  { code: 'CA', label: 'Canada' },
  { code: 'AU', label: 'Australia' },
];

/**
 * Get all jurisdiction states from the new 3-tier schema.
 * Optionally filter by countryCode.
 * Deduplicates by stateCode+country to prevent double entries from bulk imports.
 */
export async function getJurisdictionStates(countryCode?: string): Promise<Jurisdiction[]> {
  try {
    // NOTE: Do NOT apply a server-side country filter. Legacy jurisdictionStates docs were
    // seeded without a `country` field — they are implicitly US. Filtering server-side with
    // where('country', '==', 'US') would return 0 results for those legacy docs.
    // Instead, fetch all docs and filter client-side, treating missing country as 'US'.
    const q = query(collection(db, 'jurisdictionStates'), orderBy('stateCode', 'asc'));
    const snapshot = await getDocs(q);

    const raw = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Jurisdiction[];

    // Country filter applied client-side: docs without a country field default to 'US'
    const countryFiltered = countryCode
      ? raw.filter((s) => {
          const docCountry = (s as unknown as Record<string, string>).country ?? 'US';
          return docCountry === countryCode;
        })
      : raw;

    // Deduplicate by stateCode+country — keep first occurrence (lowest ID)
    const seen = new Set<string>();
    return countryFiltered.filter((s) => {
      const country = (s as unknown as Record<string, string>).country ?? 'US';
      const stateCode = (s as unknown as Record<string, string>).stateCode ?? '';
      const key = `${country}:${stateCode}`;
      if (!stateCode) return false; // skip entries with no stateCode
      if (seen.has(key)) return false;
      seen.add(key);
      // Skip entries with no useful name
      const name = (s as unknown as Record<string, string>).name || s.displayName || s.shortName;
      return !!name;
    });
  } catch (error) {
    console.error('[jurisdictionService] Failed to fetch jurisdictionStates:', error);
    return [];
  }
}

/**
 * Get jurisdiction units (counties) for a specific state
 */
export async function getJurisdictionUnits(stateCode?: string): Promise<Jurisdiction[]> {
  try {
    const constraints: QueryConstraint[] = [];
    
    if (stateCode) {
      constraints.push(where('stateCode', '==', stateCode));
    }
    
    constraints.push(orderBy('name', 'asc'));
    
    const q = query(collection(db, 'jurisdictionUnits'), ...constraints);
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Jurisdiction[];
  } catch (error) {
    console.error('[jurisdictionService] Failed to fetch jurisdictionUnits:', error);
    return [];
  }
}

/**
 * Get courts for a specific jurisdiction unit
 */
/**
 * Get courts for a specific jurisdiction unit.
 * Accepts the unit's Firestore doc ID AND, optionally, its name string.
 * Courts imported via CSV have jurisdictionUnitId set to the county name rather
 * than the Firestore doc ID, so we query with `in [docId, name]` to handle both.
 */
export async function getCourts(jurisdictionUnitId?: string, unitNameFallback?: string): Promise<Jurisdiction[]> {
  try {
    const constraints: QueryConstraint[] = [];
    
    if (jurisdictionUnitId) {
      // Build a deduped list of possible values for the jurisdictionUnitId field.
      // CSV-imported courts store the county name; UI-created courts store the doc ID.
      const lookupValues = Array.from(
        new Set([jurisdictionUnitId, ...(unitNameFallback ? [unitNameFallback] : [])])
      ).filter(Boolean);

      if (lookupValues.length === 1) {
        constraints.push(where('jurisdictionUnitId', '==', lookupValues[0]));
      } else {
        constraints.push(where('jurisdictionUnitId', 'in', lookupValues));
      }
    }
    
    constraints.push(orderBy('name', 'asc'));
    
    const q = query(collection(db, 'courts'), ...constraints);
    const snapshot = await getDocs(q);
    
    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Jurisdiction[];
  } catch (error) {
    console.error('[jurisdictionService] Failed to fetch courts:', error);
    return [];
  }
}

// ============================================
// V2 WRITE FUNCTIONS
// These write to the correct v2 collections:
//   jurisdictionStates  (states/provinces)
//   jurisdictionUnits   (counties/districts)
//   courts              (individual court records)
// ============================================

export interface JurisdictionStateV2 {
  country: string;
  stateCode: string;
  name: string;
  displayName?: string;
  shortName?: string;
  isActive: boolean;
  createdAt?: unknown;
  updatedAt?: unknown;
}

export interface JurisdictionUnitV2 {
  country: string;
  stateCode: string;
  name: string;
  displayName?: string;
  shortName?: string;
  isActive: boolean;
  createdAt?: unknown;
  updatedAt?: unknown;
}

export interface CourtV2 {
  country: string;
  stateCode: string;
  jurisdictionUnitId: string;
  name: string;
  courtType?: string;
  isActive: boolean;
  clerkWebsite?: string;
  slrUrl?: string;
  eFilingSystem?: string;
  createdAt?: unknown;
  updatedAt?: unknown;
}

/** Create or update a state/province in the v2 jurisdictionStates collection */
export async function upsertJurisdictionStateV2(
  data: Omit<JurisdictionStateV2, 'createdAt' | 'updatedAt'>,
  adminUid: string,
  existingId?: string | null
): Promise<string> {
  const now = Timestamp.now();
  if (existingId) {
    await updateDoc(doc(db, 'jurisdictionStates', existingId), { ...data, updatedAt: now });
    return existingId;
  }
  const ref = await addDoc(collection(db, 'jurisdictionStates'), { ...data, createdAt: now, updatedAt: now, createdBy: adminUid });
  return ref.id;
}

/** Create or update a county/unit in the v2 jurisdictionUnits collection */
export async function upsertJurisdictionUnitV2(
  data: Omit<JurisdictionUnitV2, 'createdAt' | 'updatedAt'>,
  adminUid: string,
  existingId?: string | null
): Promise<string> {
  const now = Timestamp.now();
  if (existingId) {
    await updateDoc(doc(db, 'jurisdictionUnits', existingId), { ...data, updatedAt: now });
    return existingId;
  }
  const ref = await addDoc(collection(db, 'jurisdictionUnits'), { ...data, createdAt: now, updatedAt: now, createdBy: adminUid });
  return ref.id;
}

/** Create or update a court in the v2 courts collection */
export async function upsertCourtV2(
  data: Omit<CourtV2, 'createdAt' | 'updatedAt'>,
  adminUid: string,
  existingId?: string | null
): Promise<string> {
  const now = Timestamp.now();
  if (existingId) {
    await updateDoc(doc(db, 'courts', existingId), { ...data, updatedAt: now });
    return existingId;
  }
  const ref = await addDoc(collection(db, 'courts'), { ...data, createdAt: now, updatedAt: now, createdBy: adminUid });
  return ref.id;
}

/**
 * Migrate existing legacy `jurisdictions` docs into the v2 collections.
 * - Docs without county → jurisdictionStates (state-level)
 * - Docs with county but no courtType → jurisdictionUnits
 * - Docs with courtType → courts
 * Deduplicates by stateCode+country so repeated runs are safe.
 * Returns counts of records written.
 */
export async function migrateJurisdictionsToV2(adminUid: string): Promise<{ states: number; units: number; courts: number }> {
  const snapshot = await getDocs(collection(db, COLLECTION_NAME));
  const legacyDocs = snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as Array<Record<string, unknown>>;

  const now = Timestamp.now();
  const batch = (await import('firebase/firestore')).writeBatch(db);

  const seenStates = new Set<string>();
  let states = 0, units = 0, courts = 0;

  for (const doc of legacyDocs) {
    const country = (doc.country as string) || 'US';
    const stateCode = (doc.stateCode as string) || '';
    const county = (doc.county as string) || null;
    const courtType = (doc.courtType as string) || null;
    const name = (doc.displayName as string) || (doc.name as string) || (doc.shortName as string) || stateCode;
    const isActive = doc.isActive !== false;

    if (!stateCode) continue;

    if (!county && !courtType) {
      // State-level record
      const key = `${country}:${stateCode}`;
      if (seenStates.has(key)) continue;
      seenStates.add(key);
      const ref = (await import('firebase/firestore')).doc(collection(db, 'jurisdictionStates') as ReturnType<typeof collection>);
      batch.set(ref, {
        country, stateCode, name,
        displayName: (doc.displayName as string) || name,
        shortName: (doc.shortName as string) || stateCode,
        isActive, createdAt: now, updatedAt: now, createdBy: adminUid,
        migratedFrom: 'jurisdictions',
      });
      states++;
    } else if (county && !courtType) {
      // Unit-level record
      const ref = (await import('firebase/firestore')).doc(collection(db, 'jurisdictionUnits') as ReturnType<typeof collection>);
      batch.set(ref, {
        country, stateCode, name: county,
        displayName: (doc.displayName as string) || county,
        shortName: county,
        isActive, createdAt: now, updatedAt: now, createdBy: adminUid,
        migratedFrom: 'jurisdictions',
      });
      units++;
    } else if (courtType) {
      // Court-level record
      const ref = (await import('firebase/firestore')).doc(collection(db, 'courts') as ReturnType<typeof collection>);
      batch.set(ref, {
        country, stateCode, courtType,
        jurisdictionUnitId: county || stateCode,
        name, displayName: (doc.displayName as string) || name,
        isActive, createdAt: now, updatedAt: now, createdBy: adminUid,
        migratedFrom: 'jurisdictions',
      });
      courts++;
    }
  }

  await batch.commit();
  return { states, units, courts };
}

/**
 * Seed all 50 US states + DC into jurisdictionStates if not already present.
 * Safe to run multiple times — deduplicates by stateCode.
 */
export async function seedUSStates(adminUid: string): Promise<number> {
  const US_STATES: Array<{ code: string; name: string }> = [
    { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
    { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
    { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
    { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
    { code: 'DC', name: 'District of Columbia' }, { code: 'FL', name: 'Florida' },
    { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
    { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' },
    { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
    { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
    { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' },
    { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
    { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
    { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' },
    { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
    { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
    { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' },
    { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
    { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
    { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' },
    { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
    { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
    { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' },
    { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
    { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
    { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' },
    { code: 'WY', name: 'Wyoming' },
  ];

  // Fetch existing records to detect missing entries and abbreviated names
  const existing = await getJurisdictionStates('US');
  const existingByCode = new Map(
    existing.map(s => [(s as unknown as Record<string, string>).stateCode as string, s])
  );

  const now = Timestamp.now();
  const { writeBatch: wb, doc: docFn } = await import('firebase/firestore');
  const batch = wb(db);
  let count = 0;

  for (const state of US_STATES) {
    const existingRecord = existingByCode.get(state.code);
    if (existingRecord) {
      // Fix abbreviated names: if stored name is just the stateCode or ≤ 3 chars, update to full name
      const currentName = (existingRecord as unknown as Record<string, string>).name || existingRecord.displayName || '';
      if (!currentName || currentName.length <= 3 || currentName === state.code) {
        batch.update(
          docFn(db, 'jurisdictionStates', existingRecord.id),
          { name: state.name, displayName: state.name, updatedAt: now }
        );
        count++;
      }
      continue;
    }
    // Create new record
    const ref = docFn(collection(db, 'jurisdictionStates') as ReturnType<typeof collection>);
    batch.set(ref, {
      country: 'US', stateCode: state.code, name: state.name,
      displayName: state.name, shortName: state.code,
      isActive: true, createdAt: now, updatedAt: now, createdBy: adminUid,
    });
    count++;
  }

  if (count > 0) await batch.commit();
  return count;
}
